import * as React from 'react'
import { observer } from 'mobx-react'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { useDocumentTitle } from 'hooks/document-title'
import { ContentLayout, ContentMain } from 'components/ContentLayout'
import { useActivation } from 'hooks/mobx-hooks'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import styles from './BundleManager.sass'
import AdminBundleManagerViewModel from 'admin/bundle/BundleManagerViewModel'
import BundleManagerHeader from 'admin/bundle/components/BundleManagerHeader'
import BundleManagerContent from 'admin/bundle/components/BundleManagerContent'
import CreateBundleDialog from '../../CreateBundleDialog'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import EditBundleDialog from '../../EditBundleDialog'
import DuplicateBundleDialog from '../../DuplicateBundleDialog'
import ArchiveBundleDialog from '../../ArchiveBundleModal'
import UnarchiveBundleDialog from '../../UnarchiveBundleModal'
import BundlesEmptyState from 'admin/bundle/components/BundlesEmptyState'
import Workspace from 'iam/models/Workspace'
import { useElementSize } from 'hooks/element-size'
import BulkDuplicateBundleDialog from '../../BulkDuplicateBundleDialog'
import BulkArchiveBundleDialog from '../../BulkArchiveBundleDialog'
import BulkApplyDiscountDialog from '../../BulkApplyDiscountDialog'
import BulkEditPriceDialog from '../../BulkEditPriceDialog'

interface BundleManagerProps {
  vm: AdminBundleManagerViewModel
}

export default observer(function BundleManager({ vm }: BundleManagerProps) {
  useDocumentTitle('Bundle manager')
  useActivation(vm)

  return vm.activate.match({
    pending: () => <ContentLoading />,
    rejected: (err: any) => <ContentError error={err} />,
    resolved: () => <BundleManagerPage vm={vm} />,
  })
})

const BundleManagerPage = observer(function BundleManagerPage({
  vm,
}: BundleManagerProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  const canShowBundleManagerContent =
    vm.searcher.scrollProvider.isDone && vm.searcher.allBundles.length !== 0
  const canShowBundleEmptyState =
    vm.searcher.scrollProvider.isDone && vm.searcher.allBundles.length === 0
  const isSmallScreen = useElementSize(scrollRef).width < 420

  return (
    <div className={styles.root} ref={scrollRef}>
      <BundleManagerHeader scrollOffset={scrollOffset} vm={vm} />
      <ContentLayout
        accountForLabel
        accountForTabBar={!canShowBundleEmptyState}
        ref={scrollRef}
        noPadding={canShowBundleEmptyState || isSmallScreen}
      >
        <ContentMain constrainWidth>
          <div className={styles.bundleViewer}>
            {canShowBundleManagerContent && <BundleManagerContent vm={vm} />}
            {canShowBundleEmptyState && (
              <BundlesEmptyState
                currentWorkspace={
                  vm.workspaceFilter.type === 'AllWorkspaces'
                    ? Workspace.none
                    : vm.workspaceFilter.workspace
                }
                currentArchivedStatusTab={vm.currentArchivedStatusTab}
              />
            )}
            {!vm.searcher.scrollProvider.isDone && (
              <InfiniteScrollLoader
                scrollProvider={vm.searcher.scrollProvider}
              />
            )}
          </div>
        </ContentMain>
      </ContentLayout>
      <CreateBundleDialog vm={vm.createBundleViewModel} />
      <EditBundleDialog vm={vm.editBundleViewModel} />
      <ArchiveBundleDialog vm={vm.archiveBundleViewModel} />
      <UnarchiveBundleDialog vm={vm.unarchiveBundleViewModel} />
      <DuplicateBundleDialog vm={vm.duplicateBundleViewModel} />
      <BulkDuplicateBundleDialog vm={vm.bulkDuplicateBundleViewModel} />
      <BulkArchiveBundleDialog vm={vm.bulkArchiveBundleViewModel} />
      <BulkEditPriceDialog vm={vm.bulkEditPriceViewModel} />
      <BulkApplyDiscountDialog vm={vm.bulkApplyDiscountViewModel} />
    </div>
  )
})
